/**
 * The virtual account page's "Suggested envelopes" (TMMAPP-0141 §B, the setup wizard's step 6 in its
 * home): a quiet card for the owner, only while the history has envelope proposals. Create, Skip and
 * Create all act as they did in the wizard, and every one is created in THIS virtual account, so no
 * `VIRTUAL_ACCOUNT_REQUIRED` choice is ever needed. Nothing is created until the person does.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { genesisApi } from '@/api/genesis';
import { useNotify } from '@/shell/NotifyProvider';
import { EnvelopeProposalsStep } from './EnvelopeProposalsStep';
import { focusAfterRemoval } from './focusAfterRemoval';
import type { GenesisProposals } from './useGenesisProposals';

interface SuggestedEnvelopesProps {
  virtualAccountId: number;
  /** An envelope was created: the page reads the virtual account again. */
  onCreated: () => void;
  /** The proposals, read by the page: their count is its Items to review count (TMMAPP-0159 §F 30). */
  source: GenesisProposals;
}

export function SuggestedEnvelopes({
  virtualAccountId,
  onCreated,
  source,
}: SuggestedEnvelopesProps) {
  const { t } = useTranslation();
  const notify = useNotify();
  const headingId = useId();
  const { proposals, groups, reload } = source;
  const [busy, setBusy] = useState(false);
  // The acted-on row's index: once the write and the re-read settle (the buttons are enabled
  // again), focus moves to the row now in its place, or to the page's h1 (TMMAPP-0143 item 2).
  const sectionRef = useRef<HTMLElement | null>(null);
  const focusIndex = useRef<number | null>(null);
  useEffect(() => {
    if (busy || focusIndex.current === null) return;
    const index = focusIndex.current;
    focusIndex.current = null;
    focusAfterRemoval(sectionRef.current, index);
  }, [busy, proposals]);

  if (proposals.length === 0) return null;

  const create = (id: number) =>
    genesisApi.accept(id, { envelope: { virtualAccountId } }).then(() => undefined);

  const refresh = async (created: boolean, index: number) => {
    if (created) onCreated();
    focusIndex.current = index;
    await reload();
  };

  const indexOf = (id: number) => proposals.findIndex((p) => p.id === id);

  const fail = (error: unknown) =>
    notify(errorCopy(t, error, 'accounts.errorGeneric'), { severity: 'error' });

  return (
    <Card
      ref={sectionRef}
      variant="outlined"
      component="section"
      aria-labelledby={headingId}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography id={headingId} variant="h2">
              {t('genesis.suggestedEnvelopes', { count: proposals.length })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('genesis.suggestedEnvelopesBody')}
            </Typography>
          </Stack>
          <EnvelopeProposalsStep
            proposals={proposals}
            groups={groups}
            busy={busy}
            onAccept={(id) => {
              setBusy(true);
              void create(id)
                .then(async () => {
                  notify(t('genesis.envelopeCreated'));
                  await refresh(true, indexOf(id));
                })
                .catch(fail)
                .finally(() => setBusy(false));
            }}
            onDismiss={(id) => {
              setBusy(true);
              void genesisApi
                .dismiss(id)
                .then(() => refresh(false, indexOf(id)))
                .catch(fail)
                .finally(() => setBusy(false));
            }}
            // Accept-all takes no virtual account: each proposal is sent with this one, in order,
            // stopping at the first refusal.
            onAcceptAll={() => {
              const asked = proposals.length;
              setBusy(true);
              void (async () => {
                let created = 0;
                try {
                  for (const proposal of proposals) {
                    await create(proposal.id);
                    created += 1;
                  }
                  notify(t('genesis.acceptedAllEnvelopes', { count: created, asked }));
                } catch (error) {
                  fail(error);
                } finally {
                  await refresh(created > 0, 0);
                  setBusy(false);
                }
              })();
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
